// The cassette — shared infrastructure, not splash-local.
//
// Lifted out of SplashPage when the tape-transport transition was authored:
// the splash mark and the Welcome → Menu transition must be visibly the SAME
// tape, still running, or the concept doesn't pay off. Two independent
// drawings of a cassette would read as two cassettes.
//
// Spin is expressed as a *rate* (rotations per second) rather than a boolean,
// and the component smooths toward whatever rate it is given, so callers state
// intent ("spin up to 3 rps", "coast to a stop") and get mechanical inertia for
// free. A boolean could only ever cut between spinning and not.
//
// The rate may be passed as a plain number OR as a ref. The ref form exists for
// the transport, which drives the whole sequence from its own requestAnimation-
// Frame loop and must be able to change the reel speed mid-flight without
// forcing a React render.
//
// Everything here is driven by direct attribute writes rather than a animation
// library: the reels, and the sag of the exposed tape between them, are
// physical state sampled per frame.

import { useEffect, useRef } from 'react';
import { colors } from '../../theme/tokens';
import type { MotionTier } from '../../theme/motion';

/** How fast the smoothed rate chases the target. Higher = less inertia. */
const RATE_INERTIA = 3.2;
/** Below this, treat the reel as stopped. */
const RATE_EPSILON = 0.004;

/** Reel geometry, shared by the drawing and the frame loop. */
const HUB_LEFT = { cx: 78, cy: 108 };
const HUB_RIGHT = { cx: 162, cy: 108 };
/** Sag of the exposed tape at rest, in viewBox units. Pulls flat under load. */
const SLACK = 14;

type RateSource = number | React.MutableRefObject<number>;

interface CassetteProps {
  /** Rotations per second, or a ref holding it. 0 coasts to a stop. */
  rate?: RateSource;
  /** Alias for `rate` when passing a ref — clearer at the call site. */
  rateRef?: React.MutableRefObject<number>;
  tier: MotionTier;
  className?: string;
  label?: string;
}

function readRate(src: RateSource | undefined): number {
  if (typeof src === 'number') return src;
  if (src && typeof src === 'object' && 'current' in src) return src.current || 0;
  return 0;
}

/**
 * Procedural cassette body — SVG, drawn at 240×160 and scaled via viewBox so it
 * stays crisp at any DPR or size. No sprite sheet, per the project's
 * procedural-rendering rule.
 */
export function Cassette({
  rate,
  rateRef,
  tier,
  className,
  label = 'CVE',
}: CassetteProps) {
  const source: RateSource | undefined = rateRef ?? rate;

  const leftReel = useRef<SVGGElement | null>(null);
  const rightReel = useRef<SVGGElement | null>(null);
  const tape = useRef<SVGPathElement | null>(null);

  // Reduced motion never rotates. The reel is still drawn — identical graphic
  // payload — it just sits still, and we never start a frame loop for it.
  const allowSpin = tier !== 'reduced';

  // Ref so the loop always reads the latest target without re-subscribing.
  const target = useRef(readRate(source));
  target.current = readRate(source);

  useEffect(() => {
    if (!allowSpin) return;

    let raf = 0;
    let last = performance.now();
    let smoothed = 0;
    let angle = 0;

    const tick = (now: number) => {
      const dt = Math.min(now - last, 64) / 1000; // clamp tab-restore spikes
      last = now;

      // Exponential approach — the reel takes a moment to reach speed and
      // coasts down instead of stopping dead.
      smoothed += (target.current - smoothed) * Math.min(1, RATE_INERTIA * dt);
      if (Math.abs(smoothed) < RATE_EPSILON && target.current === 0) smoothed = 0;

      if (smoothed !== 0) {
        angle = (angle + smoothed * 360 * dt) % 360;
        if (leftReel.current) {
          leftReel.current.setAttribute(
            'transform',
            `rotate(${angle} ${HUB_LEFT.cx} ${HUB_LEFT.cy})`,
          );
        }
        if (rightReel.current) {
          // Counter-rotation reads as tape moving from one hub to the other.
          rightReel.current.setAttribute(
            'transform',
            `rotate(${-angle} ${HUB_RIGHT.cx} ${HUB_RIGHT.cy})`,
          );
        }
      }

      // Tape tension follows reel speed: slack at rest, pulled straight once
      // the transport is up to speed.
      if (tape.current) {
        const tension = Math.min(1, Math.abs(smoothed) / 1.2);
        const sag = SLACK * (1 - tension);
        tape.current.setAttribute(
          'd',
          `M ${HUB_LEFT.cx} ${HUB_LEFT.cy} Q 120 ${HUB_LEFT.cy + sag} ${HUB_RIGHT.cx} ${HUB_RIGHT.cy}`,
        );
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [allowSpin]);

  return (
    <svg
      viewBox="0 0 240 160"
      className={className}
      role="img"
      aria-label="Cholos vs Emos cassette tape"
    >
      <rect
        x="2"
        y="2"
        width="236"
        height="156"
        rx="6"
        fill={colors.stucco2}
        stroke={colors.edge}
        strokeWidth="1.5"
      />
      <rect x="2" y="2" width="236" height="3" fill={colors.void} opacity="0.4" />

      {/* Label panel */}
      <rect
        x="22"
        y="20"
        width="196"
        height="46"
        rx="2"
        fill={colors.stucco1}
        stroke={colors.edge}
        strokeWidth="1"
      />
      <rect x="22" y="33" width="196" height="1.5" fill={colors.sodium} opacity="0.7" />
      <rect x="22" y="52" width="196" height="1.5" fill={colors.sodium} opacity="0.4" />
      <text
        x="120"
        y="49"
        textAnchor="middle"
        fill={colors.sodium}
        fontFamily="JetBrains Mono, monospace"
        fontSize="14"
        letterSpacing="4"
        fontWeight="700"
      >
        {label}
      </text>

      {/* Exposed tape spanning the hubs — sags at rest, straightens under load */}
      <path
        ref={tape}
        d={`M ${HUB_LEFT.cx} ${HUB_LEFT.cy} Q 120 ${HUB_LEFT.cy + SLACK} ${HUB_RIGHT.cx} ${HUB_RIGHT.cy}`}
        fill="none"
        stroke={colors.void}
        strokeWidth="3.5"
        opacity="0.85"
      />

      <Spool hub={HUB_LEFT} reelRef={leftReel} />
      <Spool hub={HUB_RIGHT} reelRef={rightReel} />

      {/* Screw marks — small authenticity details */}
      <circle cx="14" cy="148" r="2" fill={colors.void} />
      <circle cx="226" cy="148" r="2" fill={colors.void} />
      <circle cx="14" cy="12" r="2" fill={colors.void} />
      <circle cx="226" cy="12" r="2" fill={colors.void} />
    </svg>
  );
}

/**
 * One spool reel: recessed dark window, hub, and six radiating spokes. The
 * spokes are what make rotation legible — a plain circle spinning is invisible.
 */
function Spool({
  hub,
  reelRef,
}: {
  hub: { cx: number; cy: number };
  reelRef: React.MutableRefObject<SVGGElement | null>;
}) {
  const { cx, cy } = hub;
  return (
    <g>
      <circle cx={cx} cy={cy} r="22" fill={colors.void} stroke={colors.edge} strokeWidth="1" />
      <g ref={reelRef}>
        <circle
          cx={cx}
          cy={cy}
          r="8"
          fill={colors.stucco3}
          stroke={colors.graphite}
          strokeWidth="0.8"
        />
        {[0, 60, 120, 180, 240, 300].map((deg) => {
          const rad = (deg * Math.PI) / 180;
          return (
            <line
              key={deg}
              x1={cx}
              y1={cy}
              x2={cx + Math.cos(rad) * 18}
              y2={cy + Math.sin(rad) * 18}
              stroke={colors.graphite}
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          );
        })}
      </g>
    </g>
  );
}
